import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PDKEditIcon, PDKLinkIcon } from '@/components/ui/pdk-icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useHoverOrOpenShow from '@/components/ux/hooks/use-hover-or-open-show';
import { useGetTradeCommentUrl } from '@/hooks/use-get-trade-comment-url';
import { toast } from '@/hooks/use-toast';
import { useUser } from '@/hooks/use-user';
import { cn } from '@/lib/utils';
import type { Comment } from '@/models/registry';
import type { Trade } from '@/models/types';
import { Ellipsis } from 'lucide-react';
import { useState } from 'react';

const TradeCommentMenu = ({
  comment,
  tradeShortId,
  hoverRef,
  setIsEditingComment,
}: {
  comment: Comment;
  tradeShortId: Trade['short_id'];
  hoverRef: React.RefObject<HTMLDivElement | null>;
  setIsEditingComment: (isEditing: boolean) => void;
}) => {
  const tradeCommentUrl = useGetTradeCommentUrl(tradeShortId, comment);
  const [open, setOpen] = useState(false);

  const { shouldShow: shouldShowMenu } = useHoverOrOpenShow({
    hoverGroupRef: hoverRef,
    open,
  });

  const user = useUser();

  const isUserComment = comment.user_id === user.id;

  return (
    <div
      className={cn({
        'opacity-0': !shouldShowMenu,
        'opacity-100': shouldShowMenu,
      })}
    >
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <div>
            <Tooltip delayDuration={1000}>
              <TooltipTrigger asChild>
                <Button variant="ghost" className="p-1 h-fit">
                  <Ellipsis className={cn('h-3.5 w-3.5 cursor-pointer', {})} />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="flex flex-row gap-1 items-center">
                Comment Actions Menu
              </TooltipContent>
            </Tooltip>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          {isUserComment && (
            <DropdownMenuItem
              className="flex flex-row gap-2"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();

                setIsEditingComment(true);

                setOpen(false);
              }}
            >
              <PDKEditIcon className="size-4 cursor-pointer" />
              <div>Edit</div>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="flex flex-row gap-2"
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
              e.preventDefault();
              e.stopPropagation();

              if (!tradeCommentUrl) {
                toast({
                  title: 'Sorry no url to comment could be generated',
                });
                return;
              }

              navigator.clipboard.writeText(tradeCommentUrl);

              toast({
                title: 'Link to comment copied to clipboard',
              });
            }}
          >
            <PDKLinkIcon className="size-4 cursor-pointer" />
            <div>Copy link to comment</div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default TradeCommentMenu;
